"use client";

import cn from "classnames";
import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { CarouselImage } from "@/src/component/partial/CarouselImage";
import type { CarouselImageType } from "@/src/component/partial/CarouselImage/CarouselImage";
import { TextBreakpointModule } from "@/src/component/partial/__module__";
import { ProviderHooks } from "@/src/hook";
import { CMSLogic } from "@/src/logic";

import { LoadFromCDN } from "@/src/util";
import { StrapiCollectionType, StrapiComponentType } from "@/src/type";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./GalleryDialog.module.scss";

export default function GalleryDialog() {
  const [open, setOpen] = useState(false);
  const [gallery, setGallery] = useState<StrapiCollectionType.Gallery | undefined>(undefined);
  const { galleries, webConfig } = ProviderHooks.useCMS();
  const quality = webConfig?.defaultImageQuality ?? 100;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageSize, setImageSize] = useState<number>(1920);

  /**
   * handler to check if gallery should open
   * use click + hashchange b/c sometimes hashchange is not triggered
   * this only activates if galleries are registered to a page
   */
  useEffect(() => {
    if (!galleries || galleries.length === 0 || open) {
      return;
    }

    function handleHashChange() {
      const hash = window.location.hash;
      if (hash) {
        const hashParts = new URLSearchParams(hash.replace("#", ""));
        for (const key of Array.from(hashParts.keys())) {
          if (key.startsWith("galleries")) {
            const gallerySlug = key.replace("galleries", "").replace("[", "").replace("]", "");
            const gallery = (galleries ?? []).find((g) => g.slug === gallerySlug);
            const index = hashParts.get(key);
            if (gallery) {
              const w = window.innerWidth ?? 1920;
              const imageSize = w < 660 ? 660 : w < 1025 ? 1200 : 1920;
              setImageSize(imageSize);
              setGallery(gallery);
              setOpen(true);
              setCurrentIndex(index ? parseInt(index) : 0);
            }
          }
        }
      }
    }

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("click", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("click", handleHashChange);
    };
  }, [galleries, open]);

  function getAlt(item: StrapiComponentType.Object.GalleryItem) {
    const alt = item.alt ?? item.image?.data?.attributes?.alternativeText ?? `View Gallery ${item.image?.data?.id}`;
    let captionText = "";
    for (const text of item.captionText ?? []) {
      captionText += text.text;
    }
    return captionText ?? alt;
  }

  function getCaptionDefaultStyle() {
    switch (gallery?.galleryItems?.[0]?.captionDefaultStyle) {
      case "Category|Subject|NameAndAuthor":
        return styles.subjectNameAuthor;
      default:
        return;
    }
  }

  const galleryImages: CarouselImageType[] = useMemo(
    () =>
      gallery?.galleryItems?.map((item) => ({
        url: LoadFromCDN.imageQualityAndSizeOptional(item.image?.data?.attributes?.url, quality, imageSize),
        alt: getAlt(item),
        id: `image_gallery_${item.image?.data?.id}`,
      })) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gallery, quality, imageSize]
  );

  const currentCaption = useMemo(() => {
    const item = gallery?.galleryItems?.[currentIndex];
    return {
      captionText: item?.captionText ?? [],
      captionBgColor: item?.captionBgColor,
    };
  }, [gallery, currentIndex]);

  const handleSlideChange = (index: number) => {
    setCurrentIndex(index);
  };

  function handleClose() {
    setOpen(false);
    window.location.hash = "";
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
    >
      <div className={styles.imageDialogContainer}>
        <div
          className={styles.imageDialogExit}
          onClick={handleClose}
        >
          <CloseIcon
            fontSize="large"
            sx={{ color: theme.secondaryMain }}
          />
        </div>
        <div className={styles.imageDialogCarousel}>
          <CarouselImage
            index={currentIndex}
            images={galleryImages}
            autoPlay={false}
            imageClassName={styles.imageDialogImage}
            navButtonsAlwaysVisible={true}
            animation="slide"
            objectFit="contain"
            bgResponsive={true}
            onSlideChange={handleSlideChange}
          />
        </div>
        {(currentCaption.captionText ?? []).length > 0 && (
          <div
            className={cn(styles.captionStripe, getCaptionDefaultStyle())}
            style={{ backgroundColor: CMSLogic.colorSelector(currentCaption.captionBgColor) }}
          >
            {currentCaption.captionText.map((text) => (
              <TextBreakpointModule
                key={text.id}
                id={`GalleryDialog_Caption_${text.id}`}
                text={text}
                className={cn(styles.captionText, getCaptionDefaultStyle())}
              />
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
